import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Element } from 'react-scroll';
import { ReactSVG } from 'react-svg';
import isEmpty from 'lodash/isEmpty';
import {
  FaTint,
  FaShower,
  FaRestroom,
  FaTools,
  FaUtensils,
  FaShip,
  FaGasPump,
  FaFire,
  FaPlug,
  FaUserClock,
  FaArrowAltCircleDown,
  FaWifi,
  FaChild,
  FaSwimmingPool
} from 'react-icons/fa';
import {
  MdLocalLaundryService,
  MdLocalGroceryStore,
  MdLocalPharmacy,
  MdSurfing
} from 'react-icons/md';
import { RiHotelFill } from 'react-icons/ri';

import useWindowSize from 'library/hooks/useWindowSize';
import Heading from 'components/UI/Heading/Heading';
import IconCard from 'components/IconCard/IconCard';
import Button from 'components/UI/Antd/Button/Button';
import AmenitiesWrapper, { AmenitiesArea } from './Amenities.style';
import { TextButton } from '../SinglePageView.style';

import securityIcon from 'assets/images/security-icon.svg';
import zooIcon from 'assets/images/zoo-icon.svg';
import atmIcon from 'assets/images/atm-icon.svg';

const scrollToRef = ref => window.scrollTo(0, ref.current.offsetTop);

const Amenities = ({ amenities, titleStyle }) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const [expand, setExpand] = useState(true);
  const amenitiesWrapperRef = useRef(null);
  const executeScroll = () => scrollToRef(amenitiesWrapperRef);
  if (!amenities || isEmpty(amenities)) {
    return null;
  }
  const items = {
    wifi: { icon: <FaWifi />, title: t('WIFI') },
    supermarket: { icon: <MdLocalGroceryStore />, title: t('SUPERMARKET') },
    children_friendly: { icon: <FaChild />, title: t('CHILDREN_FRIENDLY') },
    pool: { icon: <FaSwimmingPool />, title: t('SWIMMING_POOL') },
    pharmacy: { icon: <MdLocalPharmacy />, title: t('PHARMACY') },
    hotel: { icon: <RiHotelFill />, title: t('HOTEL') },
    sport_activities: { icon: <MdSurfing />, title: t('SPORT_ACTIVITIES') },
    security: { icon: <ReactSVG src={securityIcon} />, title: t('SECURITY_CAMERAS') },
    zoo: { icon: <ReactSVG src={zooIcon} />, title: t('ZOO') },
    atm: { icon: <ReactSVG src={atmIcon} />, title: t('ATM_MACHINE') },
    fresh_water: { icon: <FaTint />, title: t('FRESH_WATER') },
    electricity: { icon: <FaPlug />, title: t('ELECTRICITY') },
    fuel: { icon: <FaGasPump />, title: t('FUEL') },
    gas: { icon: <FaFire />, title: t('GAS') },
    toilets: { icon: <FaRestroom />, title: t('RESTROOMS') },
    showers: { icon: <FaShower />, title: t('SHOWERS') },
    laundrette: { icon: <MdLocalLaundryService />, title: t('LAUNDRY') },
    restaurant: { icon: <FaUtensils />, title: t('RESTAURANT') },
    boat_lift: { icon: <FaShip />, title: t('BOAT_LIFT') },
    workshop: { icon: <FaTools />, title: t('WORKSHOP') },
    sewege_pump_out: {
      icon: <FaArrowAltCircleDown />,
      title: t('SEWAGE_PUMP_OUT')
    },
    access_24_hours: { icon: <FaUserClock />, title: t('ACCESS_24_HOURS') }
  };
  const toggleExpand = () => {
    setExpand(!expand);
    if (!expand) {
      executeScroll();
    }
  };
  const filteredAmenities = Object.keys(amenities).filter(
    key => amenities[key]
  );
  return (
    <Element name='amenities' className='Amenities'>
      <AmenitiesWrapper ref={amenitiesWrapperRef}>
        <Heading as='h2' content={t('AMENITIES')} {...titleStyle} />
        <AmenitiesArea
          collapsed={expand}
          height={width < 480 ? 160 : width < 768 ? 170 : 205}
        >
          {filteredAmenities.map((amenity, index) => (
            <IconCard
              key={index}
              icon={items[amenity] ? items[amenity].icon : <FaTint />}
              title={items[amenity] ? items[amenity].title : t('FRESH_WATER')}
            />
          ))}
        </AmenitiesArea>
        <TextButton>
          <Button onClick={toggleExpand}>
            {expand ? t('SHOW_ALL_AMENITIES') : t('SHOW_LESS')}
          </Button>
        </TextButton>
      </AmenitiesWrapper>
    </Element>
  );
};

Amenities.propTypes = {
  titleStyle: PropTypes.object
};

Amenities.defaultProps = {
  titleStyle: {
    color: '#2C2C2C',
    fontSize: ['17px', '20px', '25px'],
    lineHeight: ['1.15', '1.2', '1.36'],
    mb: ['14px', '20px', '30px']
  }
};

export default Amenities;
